import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import MuiButton from '@mui/material/Button'
import { Link as RouterLink } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Container from './Container'

interface CtaAction {
  label: string
  to: string
  variant?: 'contained' | 'outlined'
}

interface CallToActionProps {
  heading: string
  body?: string
  actions: CtaAction[]
  variant?: 'dark' | 'amber' | 'light' | 'mist'
}

export default function CallToAction({ heading, body, actions, variant = 'dark' }: CallToActionProps) {
  const bgMap = {
    dark: 'linear-gradient(140deg, #06141A 0%, #0B2027 60%, #112836 100%)',
    amber: 'linear-gradient(140deg, #B87333 0%, #D4924D 100%)',
    light: 'linear-gradient(180deg, #ffffff 0%, #F0F4F4 100%)',
    mist: '#F0F4F4',
  }
  const isLight = variant === 'light' || variant === 'mist'
  const textColor = isLight ? 'text.primary' : '#fff'
  const subtextColor = isLight ? 'text.secondary' : 'rgba(255,255,255,0.75)'

  return (
    <Box
      sx={{
        background: bgMap[variant],
        py: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: variant === 'mist' ? 'none' : '""',
          position: 'absolute',
          top: -80,
          right: -60,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: isLight ? 'rgba(138,154,91,0.1)' : 'rgba(255,255,255,0.08)',
        },
      }}
    >
      <Container>
        <Typography
          variant="h3"
          sx={{ color: textColor, mb: body ? 2 : 3, fontSize: { xs: '1.5rem', md: '2rem' } }}
        >
          {heading}
        </Typography>
        {body && (
          <Typography variant="body1" sx={{ color: subtextColor, mb: 3, maxWidth: 600 }}>
            {body}
          </Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {actions.map((action) => (
            <MuiButton
              key={action.to}
              component={RouterLink}
              to={action.to}
              variant={action.variant || 'contained'}
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={
                action.variant === 'outlined'
                  ? {
                      borderColor: isLight ? 'primary.main' : 'rgba(255,255,255,0.6)',
                      color: isLight ? 'primary.main' : '#fff',
                      '&:hover': {
                        borderColor: isLight ? 'primary.dark' : '#fff',
                        backgroundColor: isLight ? 'rgba(138,154,91,0.1)' : 'rgba(255,255,255,0.08)',
                      },
                    }
                  : {
                      background: variant === 'amber'
                        ? 'rgba(255,255,255,0.15)'
                        : 'linear-gradient(135deg, #B87333 0%, #D4924D 100%)',
                      color: '#fff',
                      boxShadow: '0 14px 30px rgba(0,0,0,0.18)',
                      border: variant === 'amber' ? '1.5px solid rgba(255,255,255,0.4)' : 'none',
                      '&:hover': {
                        background: variant === 'amber'
                          ? 'rgba(255,255,255,0.25)'
                          : 'linear-gradient(135deg, #9A5F22 0%, #B87333 100%)',
                      },
                    }
              }
            >
              {action.label}
            </MuiButton>
          ))}
        </Stack>
      </Container>
    </Box>
  )
}
